import { CourseFormState } from '@/client/courses';
import { EditorMainContent } from '@/components/content-editor/EditorMainContent';
import { EditorModalDialog } from '@/components/content-editor/EditorModalDialog';
import { EditorModalLayout } from '@/components/content-editor/EditorModalLayout';
import { UnsavedChangesModal } from '@/components/courses/modals/UnsavedChangesModal';
import { EditableText } from '@/ui/editable-text';
import { useRichTextToolbar } from '@/ui/rich-text';
import { FlexContainer } from '@/ui/styled-ui';
import { PencilIcon } from '@/utils/helpers';
import { DialogProps } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { QuizEditorCanvas } from './QuizEditorCanvas';
import { QuizEditorSidebar } from './QuizEditorSidebar';
import { QuizQuestionTimeline } from './QuizQuestionTimeline';
import { useQuizEditorState } from './useQuizEditorState';

const StyledDialog = styled(EditorModalDialog)`
  .p-dialog-content {
    padding: var(--medium-big-padding);
    display: flex;
    flex-direction: column;
    gap: var(--medium-big-padding);
    flex: 1;
    min-height: 0;
    overflow: auto;
  }
`;

const ModalLayout = styled(EditorModalLayout)`
  flex-shrink: 0;
`;

type QuizEditorModalProps = {
  index?: number;
  values: CourseFormState;
  visible: boolean;
  isDisabled: boolean;
  onHide: () => void;
  setFieldValue: (field: string, value: any) => void;
  setIsSubmitDisabled: (isSubmitDisabled: boolean) => void;
};

export const QuizEditorModal: React.FC<QuizEditorModalProps & DialogProps> = ({
  index = 0,
  values,
  visible,
  isDisabled,
  setFieldValue,
  setIsSubmitDisabled,
  onHide,
}) => {
  const { t } = useTranslation();

  const {
    questions,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    selectedLanguage,
    translationKey,
    availableLanguages,
    currentQuestion,
    isLoading,
    loadRevision,
    isSaving,
    isRtl,
    showUnsavedChanges,
    setShowUnsavedChanges,
    itemName,
    setItemName,
    handleClose,
    handleSave,
    handleDiscard,
    validationError,
    handleQuestionTextChange,
    handleAnswerTextChange,
    handleToggleCorrect,
    handleDeleteAnswer,
    handleAddAnswer,
    handleAnswerReorder,
    handleQuestionReorder,
    handleDeleteQuestion,
    handleAddQuestion,
    handleCopyToLanguage,
    handleLanguageChange,
    handleMediaChange,
    handleInteractiveElementChange,
    handleInteractiveElementDataChange,
    handleExplanationChange,
    handleAiCustomizationToggle,
    handleAiInstructionsChange,
  } = useQuizEditorState({ index, values, visible, setFieldValue, onHide });

  const {
    registerEditor,
    unregisterEditor,
    applyFormat,
    isActive,
    activeFormats,
  } = useRichTextToolbar();

  const headerTemplate = () => (
    <FlexContainer wrap="wrap">
      {!isLoading && (
        <h1 className="flex-1 w-full">
          <EditableText
            key={itemName}
            value={itemName}
            icon={<PencilIcon />}
            saveValue={setItemName}
          />
        </h1>
      )}
    </FlexContainer>
  );

  return (
    <>
      <StyledDialog
        blockScroll
        visible={visible}
        onHide={handleClose}
        header={headerTemplate}
        draggable={false}
        data-testid="quiz-editor-modal"
        pt={{ root: { dir: isRtl ? 'rtl' : 'ltr' } }}
      >
        {isLoading ? (
          <FlexContainer justify="center" align="center" className="h-full">
            <ProgressSpinner />
          </FlexContainer>
        ) : (
          <>
            <ModalLayout key={loadRevision} translate="no">
              <EditorMainContent>
                {currentQuestion && (
                  <QuizEditorCanvas
                    questions={questions}
                    currentQuestionIndex={currentQuestionIndex}
                    translationKey={translationKey}
                    onInteractiveElementDataChange={
                      handleInteractiveElementDataChange
                    }
                    onMediaChange={handleMediaChange}
                    totalQuestions={questions.length}
                    toolbarProps={{ applyFormat, activeFormats, isActive }}
                    registerEditor={registerEditor}
                    unregisterEditor={unregisterEditor}
                    onQuestionTextChange={handleQuestionTextChange}
                    onAnswerTextChange={handleAnswerTextChange}
                    onToggleCorrect={handleToggleCorrect}
                    onDeleteAnswer={handleDeleteAnswer}
                    onAddAnswer={handleAddAnswer}
                    onAnswerReorder={handleAnswerReorder}
                  />
                )}
              </EditorMainContent>
              {currentQuestion && (
                <QuizEditorSidebar
                  isDisabled={isDisabled}
                  setIsSubmitDisabled={setIsSubmitDisabled}
                  selectedLanguage={selectedLanguage}
                  translationKey={translationKey}
                  availableLanguages={availableLanguages}
                  onLanguageChange={handleLanguageChange}
                  onCopyToLanguage={handleCopyToLanguage}
                  currentQuestion={currentQuestion}
                  onMediaChange={handleMediaChange}
                  onInteractiveElementChange={handleInteractiveElementChange}
                  explanationText={
                    currentQuestion?.translations[translationKey]
                      ?.explanation ?? ''
                  }
                  onExplanationChange={handleExplanationChange}
                  registerEditor={registerEditor}
                  unregisterEditor={unregisterEditor}
                  isAiCustomizationEnabled={
                    currentQuestion?.isAiCustomizationEnabled !== false
                  }
                  aiCustomizationInstructions={
                    currentQuestion?.aiCustomizationInstructions ?? ''
                  }
                  onAiCustomizationToggle={handleAiCustomizationToggle}
                  onAiInstructionsChange={handleAiInstructionsChange}
                  onSave={handleSave}
                  isSaving={isSaving}
                />
              )}
            </ModalLayout>

            <QuizQuestionTimeline
              questions={questions}
              currentQuestionIndex={currentQuestionIndex}
              translationKey={translationKey}
              onSelectQuestion={setCurrentQuestionIndex}
              onDeleteQuestion={handleDeleteQuestion}
              onAddQuestion={handleAddQuestion}
              onReorderQuestions={handleQuestionReorder}
            />
          </>
        )}
      </StyledDialog>

      <UnsavedChangesModal
        visible={showUnsavedChanges}
        hasErrors={!!validationError}
        message={t('content.quiz.unsavedChanges.confirm')}
        onHide={() => setShowUnsavedChanges(false)}
        onSave={handleSave}
        onDiscard={handleDiscard}
      />
    </>
  );
};
